/**
 * Doc-key helpers — shared between client and server so both halves
 * agree on the wire format for `?docKey=...`.
 *
 * Doc keys identify a Y.Doc inside a Durable Object. Each row in
 * `deck_docs` / `sg_docs` is keyed `(room_id, doc_key)`.
 *
 * The delimiter is `\x01` (SOH), matching the convention used elsewhere
 * in DO KV storage (see `party/framework/storage.ts`'s `D` constant).
 * Existing input validation (`findUnsafeName`) already rejects deck and
 * note ids that contain `\x01` or `\x00`, so prefix-based SQL `LIKE`
 * deletes are safe — a deckId can't bleed across the delimiter.
 *
 * KEY FORMS
 * ---------
 *   __root                          → metadata Y.Doc (one per room)
 *   note\x01<deckId>\x01<noteId>    → one Y.Doc per note
 *   annot\x01<deckId>               → reserved (per-deck annotations)
 *   hw\x01<deckId>                  → reserved (per-deck handwriting)
 *   embed\x01<deckId>\x01<embedId>  → reserved (per-embed Y.Doc)
 */

// SOH delimiter shared with DO KV storage (`party/framework/storage.ts`'s
// `D` constant). Inlined here so the portable engine has no import edge
// into the Cloudflare-Worker-only storage module.
const D = '\x01';

/** Doc-key reserved for the root metadata Y.Doc on every room. */
export const ROOT_DOC_KEY = '__root';

export const NOTE_DOC_PREFIX = 'note';

/** `note\x01<deckId>\x01<noteId>` — one Y.Doc per note. */
export function noteDocKey(deckId: string, noteId: string): string {
  return `${NOTE_DOC_PREFIX}${D}${deckId}${D}${noteId}`;
}

/**
 * `note\x01<deckId>\x01` — prefix that matches every note doc-key for
 * the given deck. Suitable for `LIKE 'prefix%'` and `Map<string,…>`
 * `startsWith` walks. Includes the trailing delimiter so it can't
 * accidentally match `noteDeckIdSuffix...` style ids.
 */
export function noteDocKeyPrefixForDeck(deckId: string): string {
  return `${NOTE_DOC_PREFIX}${D}${deckId}${D}`;
}

/**
 * Parse a `note\x01<deckId>\x01<noteId>` key back into its parts.
 * Returns `null` for non-note keys or malformed input.
 */
export function parseNoteDocKey(key: string): { deckId: string; noteId: string } | null {
  if (!key.startsWith(`${NOTE_DOC_PREFIX}${D}`)) return null;
  const rest = key.slice(NOTE_DOC_PREFIX.length + D.length);
  const sep = rest.indexOf(D);
  if (sep <= 0 || sep === rest.length - 1) return null;
  return {
    deckId: rest.slice(0, sep),
    noteId: rest.slice(sep + D.length),
  };
}

// ---------------------------------------------------------------------------
// Handwriting / drawing docs — the pencil & shape layer over PDF sources.
// One Y.Doc per deck holding every page's strokes (see
// `sync-client/yjs/drawing-doc.ts` for the internal Y schema).
// ---------------------------------------------------------------------------

export const HW_DOC_PREFIX = 'hw';

/** `hw\x01<deckId>` — one drawing Y.Doc per deck. */
export function hwDocKey(deckId: string): string {
  return `${HW_DOC_PREFIX}${D}${deckId}`;
}

/**
 * `hw\x01<deckId>` — prefix matching the deck's drawing doc for SQL
 * `LIKE 'prefix%'` cascade deletes. (No trailing delimiter: the key has
 * exactly one segment after the prefix.)
 */
export function hwDocKeyPrefixForDeck(deckId: string): string {
  return `${HW_DOC_PREFIX}${D}${deckId}`;
}

/** Is this docKey a drawing doc (any deck)? Used by the group-side
 *  write gate to apply draw permissions to binary frames. */
export function isHwDocKey(key: string): boolean {
  return key.startsWith(`${HW_DOC_PREFIX}${D}`);
}
